import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { cleanObject } from "src/utils/util";
import { Timestamp } from "firebase/firestore";
import { aiTopicRepository } from "../topics/ai-topic.repository";
import { Topic } from "../topics/entities/ai-topic.entity";
import { aiCoachPromptsRepository } from "./ai-coach-prompts.repository";
import {
  AICoachPrompt,
  RequestAICoachPromptReplacementPayload,
  UpsertAICoachPromptPayload,
} from "./entities/ai-coach-prompt.entity";

const aiCoachPromptsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getAICoachPrompts: builder.query<(AICoachPrompt & { id: string })[], void>({
      queryFn: async () => {
        const result = await executeQuery(() =>
          aiCoachPromptsRepository.find({
            limit: 250,
          } as any)
        );

        if ("error" in result) {
          return { error: result.error } as any;
        }

        return {
          data: (result.data as unknown) as (AICoachPrompt & { id: string })[],
        };
      },
      providesTags: ["AI_COACH_PROMPTS"],
    }),

    getAICoachPromptTopics: builder.query<(Topic & { id: string })[], void>({
      queryFn: async () => {
        const result = await executeQuery(() =>
          aiTopicRepository.find({
            limit: 250,
          } as any)
        );

        if ("error" in result) {
          return { error: result.error } as any;
        }

        return {
          data: (result.data as unknown) as (Topic & { id: string })[],
        };
      },
      providesTags: ["AI_TOPICS"],
    }),

    requestAICoachPromptReplacement: builder.mutation<void, RequestAICoachPromptReplacementPayload>({
      queryFn: async (payload) =>
        await executeQuery(async () => {
          await aiCoachPromptsRepository.setById(
            payload.promptKey,
            cleanObject({
              requestStatus: "PENDING",
              requestedPrompt: payload.requestedPrompt,
              requestedAt: payload.requestedAt
                ? Timestamp.fromDate(new Date(payload.requestedAt))
                : Timestamp.now(),
              requestedByAdminId: payload.requestedByAdminId,
              requestedByAdminName: payload.requestedByAdminName,
              requestNote: payload.requestNote,
            }),
            { merge: true }
          );

          return undefined;
        }),
      invalidatesTags: ["AI_COACH_PROMPTS"],
    }),

    upsertAICoachPrompts: builder.mutation<void, UpsertAICoachPromptPayload[]>({
      queryFn: async (prompts) =>
        await executeQuery(async () => {
          await Promise.all(
            prompts.map((prompt) =>
              aiCoachPromptsRepository.setById(
                prompt.promptKey,
                {
                  promptKey: prompt.promptKey,
                  promptType: prompt.promptType,
                  title: prompt.title,
                  currentPrompt: prompt.currentPrompt,
                  requestStatus: prompt.requestStatus || "IDLE",
                  updatedAt: Timestamp.now(),
                },
                { merge: true }
              )
            )
          );

          return undefined;
        }),
      invalidatesTags: ["AI_COACH_PROMPTS"],
    }),
  }),
});

export const {
  useGetAICoachPromptsQuery,
  useGetAICoachPromptTopicsQuery,
  useRequestAICoachPromptReplacementMutation,
  useUpsertAICoachPromptsMutation,
} = aiCoachPromptsService;
